// =============================================================================
// POWIĄZANIA KART — logika zakładki „Organizacja › Obieg dokumentów › Powiązania kart”
// (UD-22b). Moduł BEZ Reacta i bez Supabase: zdania, listy i decyzje ekranu liczone
// czystymi funkcjami, żeby dało się je mierzyć bez renderowania.
//
// LISTY SĄ KOPIĄ. Klasy i powody pochodzą z CHECK-ów migracji 812
// (`kartoteka_propozycje_klasa_chk`, `…_powody_chk`), stany źródła z CHECK-a 804
// (`kartoteka_zrodla_stan_chk`), a reguły przyjęcia z receptor-core
// (`KLASY_PRZYJMOWALNE`, `ZGODY_ROZSTRZYGNIECIA`, `POWODY_BEZ_ZGODY`). Powłoka (aplikacja
// Vite) nie importuje niczego z `services/*`, więc zgodność co do znaku pilnuje
// `tests/unit/apps/kontraktPowiazanKart.test.ts`. Klasa albo powód bez zdania w panelu
// to wiersz, który milczy o tym, dlaczego czeka.
//
// AKCJE LICZY USŁUGA. Przyciski stoją WYŁĄCZNIE z `akcje` propozycji (te same listy co
// funkcja 834); ten plik daje im tylko słowa.
// =============================================================================

import type { PrawaObiegu } from '../devices/obiegDokumentow';
import type { ObserwacjaSpisuKartoteki, StanKartoteki } from '../devices/receptorCoreApi';

/** Klasy propozycji — CHECK `kartoteka_propozycje_klasa_chk` (812), w kolejności sekcji ekranu. */
export const KLASY_PROPOZYCJI_PANELU = [
  'zmiana_b',
  'nowa_czysta',
  'przenosiny',
  'kod_zajety',
  'nazwa_zajeta',
  'przenosiny_rozjazd',
  'ean_zajety',
  'do_potwierdzenia',
] as const;

/** Klasy przyjmowane w panelu — kopia `KLASY_PRZYJMOWALNE` z receptor-core. */
export const KLASY_PRZYJMOWALNE_PANELU = [
  'nowa_czysta',
  'przenosiny',
  'kod_zajety',
  'nazwa_zajeta',
  'zmiana_b',
] as const;

/** Powody zmiany B, na które przyjęcie jest zgodą — kopia `ZGODY_ROZSTRZYGNIECIA`. */
export const ZGODY_ROZSTRZYGNIECIA_PANELU = ['zmiana_jednostki', 'vat_0'] as const;

/** Powody, których przyjęcie nie obejmuje — kopia `POWODY_BEZ_ZGODY`. */
export const POWODY_BEZ_ZGODY_PANELU = ['ean_zajety'] as const;

/** Sufit powodu wyłączenia i odrzucenia — ten sam, co w usłudze i w funkcjach bazy. */
export const MAKS_DLUGOSC_POWODU_KARTOTEKI = 500;

/** Ile otwartych propozycji oddaje usługa (`LIMIT_OTWARTYCH`). */
export const LIMIT_OTWARTYCH_PANELU = 200;

export interface ZdanieKlasy {
  tytul: string;
  zdanie: string;
}

/** Zdanie każdej klasy z CHECK 812 — tytuł sekcji i jedno zdanie, dlaczego karta czeka. */
export const ZDANIA_KLAS: Record<(typeof KLASY_PROPOZYCJI_PANELU)[number], ZdanieKlasy> = {
  zmiana_b: {
    tytul: 'Zmiana karty powiązanej',
    zdanie:
      'Karta już powiązana z Receptorem zmieniła się tak, że Universe pyta o zgodę — na przykład jednostka albo stawka VAT 0%.',
  },
  nowa_czysta: {
    tytul: 'Nowe karty',
    zdanie:
      'Karty, których Universe jeszcze nie ma i które z niczym nie kolidują. Czekają, bo automat albo przyjmowanie kart jest wyłączone.',
  },
  przenosiny: {
    tytul: 'Karty z dawnego importu',
    zdanie:
      'Karta jest już w Universe z dawnego importu: ten sam numer karty w Receptorze i ta sama nazwa. Przeniesienie powiąże ją z Receptorem.',
  },
  kod_zajety: {
    tytul: 'Kod zajęty',
    zdanie:
      'Kod tej karty ma już inna karta w Universe. Kartę można przyjąć z kodem z serii — kod w Receptorze zostaje bez zmian.',
  },
  nazwa_zajeta: {
    tytul: 'Nazwa zajęta',
    zdanie: 'W Universe jest już karta o tej nazwie. Kartę z Receptora można przyjąć jako osobną.',
  },
  przenosiny_rozjazd: {
    tytul: 'Dawny import się nie zgadza',
    zdanie:
      'Karta z dawnego importu ma ten sam numer w Receptorze, ale inną nazwę, typ albo kod. Popraw kartę w Receptorze.',
  },
  ean_zajety: {
    tytul: 'EAN zajęty',
    zdanie: 'Ten EAN ma już inna karta w Universe. Popraw EAN w Receptorze.',
  },
  do_potwierdzenia: {
    tytul: 'Do poprawy w Receptorze',
    zdanie:
      'Karta ma braki albo wartości spoza zakresu. Popraw ją w Receptorze — wróci przy następnym spisie.',
  },
};

export interface ZdaniePowodu {
  /** Krótko — na plakietce. */
  etykieta: string;
  /** Dłużej — w podpowiedzi plakietki. */
  podpowiedz: string;
}

/** Zdanie każdego powodu z CHECK `kartoteka_propozycje_powody_chk` (812). */
export const ZDANIA_POWODOW: Record<string, ZdaniePowodu> = {
  brak_nazwy: { etykieta: 'Brak nazwy', podpowiedz: 'Karta w Receptorze nie ma nazwy.' },
  brak_typu: {
    etykieta: 'Brak typu',
    podpowiedz: 'Karta w Receptorze nie ma typu (wyrób, półprodukt albo surowiec).',
  },
  nazwa_rozna: {
    etykieta: 'Inna nazwa',
    podpowiedz: 'Nazwa w Receptorze różni się od nazwy karty w Universe.',
  },
  typ_rozny: {
    etykieta: 'Inny typ',
    podpowiedz: 'Typ w Receptorze różni się od typu karty w Universe.',
  },
  kod_zajety: { etykieta: 'Kod zajęty', podpowiedz: 'Ten kod ma już inna karta w Universe.' },
  nazwa_zajeta: {
    etykieta: 'Nazwa zajęta',
    podpowiedz: 'Karta o tej nazwie jest już w Universe.',
  },
  ean_zajety: { etykieta: 'EAN zajęty', podpowiedz: 'Ten EAN ma już inna karta w Universe.' },
  jednostka_nieznana: {
    etykieta: 'Nieznana jednostka',
    podpowiedz: 'Universe nie zna jednostki tej karty. Ustaw w Receptorze kg, l albo szt.',
  },
  jednostka_rozna_z_pozycja: {
    etykieta: 'Inna jednostka niż w magazynie',
    podpowiedz: 'Jednostka karty różni się od jednostki pozycji magazynowej w Universe.',
  },
  zmiana_jednostki: {
    etykieta: 'Zmiana jednostki',
    podpowiedz: 'Karta zmieniła jednostkę w Receptorze. Przyjęcie zmiany to Twoja zgoda.',
  },
  vat_0: {
    etykieta: 'VAT 0%',
    podpowiedz: 'Karta ma stawkę VAT 0%. Przyjęcie to Twoja zgoda na tę stawkę.',
  },
  vat_spoza_slownika: {
    etykieta: 'Nietypowy VAT',
    podpowiedz: 'Stawka VAT spoza 5, 8 i 23%. Sprawdź ją w Receptorze.',
  },
  zmiana_vat_na_brak: {
    etykieta: 'VAT usunięty',
    podpowiedz: 'Karta straciła stawkę VAT w Receptorze.',
  },
  masa_spoza_zakresu: {
    etykieta: 'Nietypowa masa',
    podpowiedz: 'Masa karty jest ujemna albo nierealnie duża. Popraw ją w Receptorze.',
  },
  ean_niepoprawny: {
    etykieta: 'Błędny EAN',
    podpowiedz: 'EAN karty nie jest poprawnym kodem kreskowym. Popraw go w Receptorze.',
  },
  trwalosc_spoza_zakresu: {
    etykieta: 'Nietypowa trwałość',
    podpowiedz: 'Trwałość karty jest ujemna albo nierealnie długa. Popraw ją w Receptorze.',
  },
  zmiana_typu_surowiec: {
    etykieta: 'Zmiana na surowiec',
    podpowiedz: 'Karta wyrobu albo półproduktu zmieniła w Receptorze typ na surowiec.',
  },
  surowiec_bez_przyjecia: {
    etykieta: 'Surowiec',
    podpowiedz:
      'Dawniej Universe nie przyjmował surowców. Przy następnym spisie karta zostanie oceniona od nowa.',
  },
  zrodlo_nieaktywne: {
    etykieta: 'Przyjmowanie wyłączone',
    podpowiedz: 'Przyjmowanie kart z Receptora było wyłączone, gdy karta przyszła.',
  },
  automat_wylaczony: {
    etykieta: 'Automat wyłączony',
    podpowiedz: 'Automat jest wyłączony, więc nowe karty przyjmuje człowiek.',
  },
  przenosiny_niezatwierdzone: {
    etykieta: 'Przenosiny niezatwierdzone',
    podpowiedz: 'Przeniesienie kart z dawnego importu nie zostało jeszcze zatwierdzone.',
  },
  ean_rozny_z_pozycja: {
    etykieta: 'Inny EAN niż w magazynie',
    podpowiedz: 'EAN karty różni się od EAN-u pozycji magazynowej w Universe.',
  },
  vat_rozny_z_pozycja: {
    etykieta: 'Inny VAT niż w magazynie',
    podpowiedz: 'Stawka VAT karty różni się od stawki pozycji magazynowej w Universe.',
  },
  kod_pary_rozny: {
    etykieta: 'Inny kod niż w imporcie',
    podpowiedz: 'Kod karty różni się od kodu karty z dawnego importu.',
  },
};

/** Powód spoza listy (CHECK rozszerzony migracją nowszą niż panel) — nazwany, nie ukryty. */
export function zdaniePowodu(powod: string): ZdaniePowodu {
  return (
    ZDANIA_POWODOW[powod] ?? {
      etykieta: powod,
      podpowiedz: 'Powód, którego ten panel jeszcze nie zna — zgłoś go Insolitum.',
    }
  );
}

export function zdanieKlasy(klasa: string): ZdanieKlasy {
  return (
    (ZDANIA_KLAS as Record<string, ZdanieKlasy>)[klasa] ?? {
      tytul: klasa,
      zdanie: 'Klasa, której ten panel jeszcze nie zna — zgłoś ją Insolitum.',
    }
  );
}

/** Stany źródła — CHECK `kartoteka_zrodla_stan_chk` (804). */
export const STANY_ZRODLA_PANELU = [
  'nieaktywne',
  'aktywne',
  'inna_instalacja',
  'epoka_zmieniona',
  'instalacja_nie_podaje',
] as const;

export const ZDANIA_STANOW_ZRODLA: Record<(typeof STANY_ZRODLA_PANELU)[number], string> = {
  nieaktywne: 'Przyjmowanie kart z Receptora jest wyłączone. Karty czekają na włączenie.',
  aktywne: 'Universe przyjmuje karty z Receptora.',
  inna_instalacja:
    'Agent zgłosił inną instalację Receptora niż ta, z której pochodzi kartoteka. Universe wstrzymał przyjmowanie kart.',
  epoka_zmieniona:
    'Baza Receptora została odtworzona albo wymieniona. Universe wstrzymał przyjmowanie kart.',
  instalacja_nie_podaje:
    'Receptor nie podaje identyfikatora instalacji. Universe wstrzymał przyjmowanie kart.',
};

/** Zdanie stanu źródła. Stan spoza listy jest nazwany i traktowany jak zawieszony. */
export function zdanieStanuZrodla(stan: string): string {
  return (
    (ZDANIA_STANOW_ZRODLA as Record<string, string>)[stan] ??
    `Nieznany stan źródła (${stan}). Universe nie przyjmuje kart.`
  );
}

/** Źródło zawieszone — przyjmowania nie włączy właściciel; przepina Insolitum. */
export function czyZrodloZawieszone(stan: string): boolean {
  return stan !== 'aktywne' && stan !== 'nieaktywne';
}

export const ZDANIE_PRZEPIECIE_INSOLITUM = 'Przepięcie instalacji wykonuje Insolitum';

/** Słowo na przycisku przyjęcia — wyłącznie dla klas przyjmowalnych; inne nie mają przycisku. */
export function etykietaPrzyjecia(klasa: string): string | null {
  switch (klasa) {
    case 'kod_zajety':
      return 'Przyjmij z kodem z serii';
    case 'nazwa_zajeta':
      return 'Przyjmij jako osobną kartę';
    case 'zmiana_b':
      return 'Przyjmij zmianę';
    case 'przenosiny':
      return 'Przenieś kartę';
    case 'nowa_czysta':
      return 'Przyjmij kartę';
    default:
      return null;
  }
}

export const ZDANIE_AUTOMAT_WYMAGA_VAT = 'Automat wymaga potwierdzenia stawek VAT.';

/**
 * Formularz włączenia: automat wymaga potwierdzenia VAT ZA KAŻDYM RAZEM — także wtedy,
 * gdy VAT potwierdzono wcześniej (funkcja 812 odmawia `vat_niepotwierdzony` bez wyjątku).
 */
export function walidujWlaczenie(w: {
  automat: boolean;
  vatZReceptora: boolean;
}): { ok: true } | { ok: false; powod: string } {
  if (w.automat && !w.vatZReceptora) return { ok: false, powod: ZDANIE_AUTOMAT_WYMAGA_VAT };
  return { ok: true };
}

export type StanZakladkiPowiazan =
  | 'brak_prawa'
  | 'bez_organizacji'
  | 'brak_uslugi'
  | 'ladowanie'
  | 'blad'
  | 'brak_zrodla'
  | 'gotowe';

/**
 * Stan zakładki. Bez prawa decyzji i bez usługi zakładka NIE PYTA — usługa odmówiłaby
 * 403 albo nie ma jej wcale, a ekran ma powiedzieć dlaczego, zamiast pokazać błąd.
 */
export function stanZakladki(w: {
  prawa: PrawaObiegu;
  organizacja: boolean;
  uslugaDostepna: boolean;
  dane: StanKartoteki | 'blad' | null;
}): StanZakladkiPowiazan {
  if (!w.prawa.decyduje) return 'brak_prawa';
  if (!w.organizacja) return 'bez_organizacji';
  if (!w.uslugaDostepna) return 'brak_uslugi';
  if (w.dane === null) return 'ladowanie';
  if (w.dane === 'blad') return 'blad';
  if (w.dane.zrodlo === null) return 'brak_zrodla';
  return 'gotowe';
}

/**
 * Zdanie bezpiecznika nagrobka (832) — tylko przy wstrzymanej obserwacji, inaczej `null`.
 * Progi: więcej niż 5 kart i więcej niż 10% mapy.
 */
export function zdanieBezpiecznika(o: ObserwacjaSpisuKartoteki | null): string | null {
  if (!o || 'pominieta' in o || o.wstrzymana !== true) return null;
  const brak = o.brak ?? '?';
  const mapa = o.mapa ?? '?';
  return `Ostatni spis nie zawierał ${brak} z ${mapa} kart — więcej niż 5 i więcej niż 10%. Universe wstrzymał oznaczanie kart jako usuniętych, żeby błąd odczytu nie skasował kartoteki.`;
}

/** Typ karty z kartoteki/2 po polsku. */
export function nazwaTypu(typ: string | null): string {
  switch (typ) {
    case 'wyrob':
      return 'Wyrób';
    case 'polprodukt':
      return 'Półprodukt';
    case 'surowiec':
      return 'Surowiec';
    default:
      return typ ?? '—';
  }
}
